import { useCart } from '@/contexts/CartContext';
import { Check, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Activity, BarChart3, Layers, Eye } from 'lucide-react';
import { useState } from 'react';

const INDICATORS_DATA = [
  {
    slug: 'arbiedge-pro',
    name: 'ArbiEdge Pro Indicator',
    subtitle: 'Triangular Arbitrage Visualization',
    price: 97,
    platform: 'MT5',
    image_url: 'https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=800',
    features: [
      'Tracks EURUSD, EURGBP & GBPUSD simultaneously',
      'Profit curve with Max/Min/Midpoint reference lines',
      'BUY and SELL mode analysis',
      'Includes .mq5 source + PDF User Guide',
    ],
  },
  {
    slug: 'traffic-light-suite',
    name: 'Traffic Light Suite',
    subtitle: 'Multi-Timeframe Trade Filter',
    price: 147,
    platform: 'MT5',
    image_url: 'https://images.unsplash.com/photo-1643962577481-4ff81600e439?w=800',
    features: [
      '4 indicators: 1Min, 5Min, 15Min & 1Hr filters',
      'Instant visual signals: Red / Orange / Lime',
      '3-stage pullback entry sequencer on 5Min',
      'Includes 4 .mq5 files + Suite Overview PDF',
    ],
  },
];

const CHART_SIGNALS = [
  { label: 'Profit Curve Rising', desc: 'Divergence increasing — arbitrage becoming more profitable', icon: TrendingUp, color: 'text-emerald-400' },
  { label: 'Profit Curve Falling', desc: 'Pairs re-aligning — opportunity closing', icon: TrendingDown, color: 'text-red-400' },
  { label: 'At Max Line', desc: 'Historical peak divergence — potential mean-reversion', icon: Activity, color: 'text-amber-400' },
  { label: 'At Min Line', desc: 'Tight correlation — new divergence may form', icon: BarChart3, color: 'text-sky-400' },
];

const ARBI_FAQS = [
  { q: 'What pairs does it analyze?', a: 'ArbiEdge Pro analyzes the EUR/USD, EUR/GBP, and GBP/USD triangle — the most liquid triangular arbitrage combination in forex.' },
  { q: 'What timeframe should I use?', a: 'Any timeframe. M1-M5 for frequent deviations, H1-D1 for larger structural patterns.' },
  { q: 'Is this a trading robot?', a: 'No. It visualizes arbitrage opportunities but does not execute trades.' },
  { q: 'Does it account for spreads?', a: 'It shows theoretical values. Real execution needs spreads, slippage, and speed factored in.' },
  { q: 'Does it work on MT4?', a: 'No. MT5 only — MT4 lacks multi-symbol data access.' },
];

const TL_SIGNALS = [
  { label: 'Red', desc: 'Strong opposing trend — do not enter.', color: 'bg-red-500' },
  { label: 'Orange', desc: 'Caution — trend is transitioning.', color: 'bg-orange-500' },
  { label: 'Lime / Green', desc: 'All clear — momentum aligns. Entry signal.', color: 'bg-lime-500' },
  { label: 'Misty Rose', desc: 'Neutral — market is ranging.', color: 'bg-pink-200' },
];

const TL_INDICATORS = [
  { name: '1-Minute Filter', tf: 'M1', desc: 'Micro-scalp filter for ultra-short entries.' },
  { name: '5-Minute Filter', tf: 'M5', desc: 'Primary entry with 3-stage pullback sequencer.' },
  { name: '15-Minute Filter', tf: 'M15', desc: 'Intermediate bias confirmation.' },
  { name: '1-Hour Filter', tf: 'H1', desc: 'Top-down directional bias.' },
];

const TL_FAQS = [
  { q: 'How do the 4 indicators work together?', a: 'Top-down confluence: H1 for trend, M15 for bias, M5 for entry, M1 for micro-confirmation. Trade when all agree.' },
  { q: 'What is the 3-stage pullback sequencer?', a: 'Stage 1: Impulse detected. Stage 2: Pullback into key zone. Stage 3: Resumption confirmed. Enter on pullbacks, not chasing.' },
  { q: 'What currency pairs?', a: 'All pairs, indices, commodities in MT5. Best on liquid majors.' },
  { q: 'Can I use it for scalping?', a: 'Yes. M1+M5 for scalping, M15+H1 as directional filters.' },
  { q: 'Does it repaint?', a: 'No. Signals confirmed on bar close, never change.' },
  { q: 'Does it work on MT4?', a: 'No. MT5 only.' },
];

function ArbiEdgeDetails({ openFaq, setOpenFaq }) {
  return (
    <div className="p-5 border-t border-white/[0.06]" data-testid="arbi-details-content">
      <h3 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-3">How It Works</h3>
      <p className="text-sm text-white/40 mb-3">Three related currency pairs maintain a mathematical equilibrium:</p>
      <div className="inline-block px-3 py-1.5 rounded-lg bg-amber-400/5 border border-amber-400/10 text-amber-400 font-mono text-xs mb-4">
        EUR/USD = EUR/GBP x GBP/USD
      </div>
      <p className="text-sm text-white/40 mb-5">When this relationship deviates, ArbiEdge Pro plots the cumulative theoretical profit or loss over time.</p>

      <h4 className="text-xs font-semibold text-white mb-2">Reading the Chart</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
        {CHART_SIGNALS.map((s, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <s.icon size={14} className={`${s.color} shrink-0 mt-0.5`} />
            <div>
              <p className="text-xs font-medium text-white/80">{s.label}</p>
              <p className="text-[11px] text-white/40">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <h4 className="text-xs font-semibold text-white mb-2">What's Included</h4>
      <div className="space-y-1.5 mb-5">
        <div className="flex items-center gap-2 text-xs text-white/50"><Check size={12} className="text-amber-400 shrink-0" /> ArbiEdgePro.mq5 — indicator source file</div>
        <div className="flex items-center gap-2 text-xs text-white/50"><Check size={12} className="text-amber-400 shrink-0" /> User Guide (PDF) — installation & signals</div>
      </div>

      <h4 className="text-xs font-semibold text-white mb-2">FAQ</h4>
      <div className="space-y-1.5">
        {ARBI_FAQS.map((faq, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <button
              onClick={() => setOpenFaq(openFaq === `a-${i}` ? null : `a-${i}`)}
              className="flex items-center justify-between w-full px-3 py-2 text-xs text-white/70 hover:text-white transition-colors text-left"
            >
              <span>{faq.q}</span>
              {openFaq === `a-${i}` ? <ChevronUp size={14} className="text-white/30 shrink-0 ml-2" /> : <ChevronDown size={14} className="text-white/30 shrink-0 ml-2" />}
            </button>
            {openFaq === `a-${i}` && (
              <div className="px-3 pb-2 text-[11px] text-white/40 leading-relaxed">{faq.a}</div>
            )}
          </div>
        ))}
      </div>

      <p className="mt-4 text-[10px] text-white/20">Educational & research tool. Does not execute trades. Past performance does not guarantee future results.</p>
    </div>
  );
}

function TrafficLightDetails({ openFaq, setOpenFaq }) {
  return (
    <div className="p-5 border-t border-white/[0.06]" data-testid="tl-details-content">
      <h3 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-3">How It Works</h3>
      <p className="text-sm text-white/40 mb-2">Four independent indicators — one per timeframe — give you instant visual confirmation of trade direction.</p>
      <p className="text-sm text-white/40 mb-5">The rule: <span className="text-white/70 font-medium">only trade when all timeframes agree.</span></p>

      <h4 className="text-xs font-semibold text-white mb-2">The 4 Indicators</h4>
      <div className="grid grid-cols-2 gap-2 mb-5">
        {TL_INDICATORS.map((ind, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-400/10 text-amber-400">{ind.tf}</span>
              <span className="text-xs font-semibold text-white/80">{ind.name}</span>
            </div>
            <p className="text-[11px] text-white/40">{ind.desc}</p>
          </div>
        ))}
      </div>

      <h4 className="text-xs font-semibold text-white mb-2">Signal Colors</h4>
      <div className="grid grid-cols-2 gap-2 mb-5">
        {TL_SIGNALS.map((s, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className={`w-3 h-3 rounded-full ${s.color} shrink-0 mt-0.5`} />
            <div>
              <p className="text-xs font-medium text-white/80">{s.label}</p>
              <p className="text-[11px] text-white/40">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <h4 className="text-xs font-semibold text-white mb-2">3-Stage Pullback Sequencer (M5)</h4>
      <div className="space-y-2 mb-5">
        {[
          { n: '1', t: 'Impulse Detection', d: 'Strong initial move detected and marked.' },
          { n: '2', t: 'Pullback Zone', d: 'Price retraces into key zone; reversals filtered out.' },
          { n: '3', t: 'Resumption Signal', d: 'Momentum resumes — this is your entry trigger.' },
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-400/10 text-amber-400 text-[10px] font-bold shrink-0">{step.n}</span>
            <div><p className="text-xs font-medium text-white/80">{step.t}</p><p className="text-[11px] text-white/40">{step.d}</p></div>
          </div>
        ))}
      </div>

      <h4 className="text-xs font-semibold text-white mb-2">What's Included</h4>
      <div className="space-y-1.5 mb-5">
        <div className="flex items-center gap-2 text-xs text-white/50"><Layers size={12} className="text-amber-400 shrink-0" /> 4 x .mq5 indicator files (M1, M5, M15, H1)</div>
        <div className="flex items-center gap-2 text-xs text-white/50"><Eye size={12} className="text-amber-400 shrink-0" /> Suite Overview PDF — setup & trading examples</div>
      </div>

      <h4 className="text-xs font-semibold text-white mb-2">FAQ</h4>
      <div className="space-y-1.5">
        {TL_FAQS.map((faq, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <button
              onClick={() => setOpenFaq(openFaq === `t-${i}` ? null : `t-${i}`)}
              className="flex items-center justify-between w-full px-3 py-2 text-xs text-white/70 hover:text-white transition-colors text-left"
            >
              <span>{faq.q}</span>
              {openFaq === `t-${i}` ? <ChevronUp size={14} className="text-white/30 shrink-0 ml-2" /> : <ChevronDown size={14} className="text-white/30 shrink-0 ml-2" />}
            </button>
            {openFaq === `t-${i}` && (
              <div className="px-3 pb-2 text-[11px] text-white/40 leading-relaxed">{faq.a}</div>
            )}
          </div>
        ))}
      </div>

      <p className="mt-4 text-[10px] text-white/20">Educational & research tool. Does not execute trades. Past performance does not guarantee future results.</p>
    </div>
  );
}

const STEPS = [
  { num: 1, tf: 'H1', title: 'Candle Pattern', desc: 'Identify a reversal candle pattern — Hammer or Railroad Track — on the H1 chart.' },
  { num: 2, tf: 'H1', title: 'Support or Resistance Level', desc: 'The candle pattern must occur at a key support or resistance level. This sets your trade direction.' },
  { num: 3, tf: 'H1', title: 'Confirmation Candle', desc: 'Wait for the next H1 candle to close in the same direction as Step 1.' },
  { num: 4, tf: 'H1', title: 'EMA Cross', desc: 'EMA5 crosses EMA3x3 in the trade direction.' },
  { num: 5, tf: 'H1', title: 'Stochastic Cross', desc: 'Stochastic crosses below 20 for BUY or above 80 for SELL. Both EMA and Stochastic must occur together.' },
  { num: 6, tf: 'H1', title: 'Trendline Break', desc: 'A broken trendline confirms the previous trend structure is giving way.' },
  { num: 7, tf: 'H1', title: 'MACD Crossover', desc: 'MACD crossover confirms momentum. Trendline Break + MACD — two independent confirmations.' },
  { num: 8, tf: 'M15', title: 'Price vs 40 EMA', desc: 'Price on M15 must close on the correct side of the 40 EMA.' },
  { num: 9, tf: 'M5', title: 'EMA5 + EMA11 Cross EMA62', desc: 'The final trigger. Both EMA5 and EMA11 cross EMA62 on M5. All timeframes aligned — execute.' },
];

const EAS = [
  { num: 1, name: '9StepBowtie', level: 'Scalping', desc: 'H1 EMA cross scalping alerts. Learn the "Bowtie" pattern.', tier: 'Bowtie' },
  { num: 2, name: '9StepBowtie1', level: 'Scalping+', desc: 'Adds M15 bias filter + M5 gap check for scalp entries.', tier: 'Bowtie' },
  { num: 3, name: '9StepBowtie2', level: 'Day Trading', desc: 'Sequential H1 -> M15 -> M5 confirmation for day trades.', tier: '9-Step' },
  { num: 4, name: '9Step1', level: 'Visual', desc: 'On-chart step panel. See which day trading steps are complete.', tier: '9-Step' },
  { num: 5, name: '9StepSystem', level: 'Full Auto', desc: "Complete 7-step day trading automation with 'Prompt' mode.", tier: '9-Step Pro' },
];

export default function IndicatorsPage() {
  const { addItem, items } = useCart();
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedSlug, setExpandedSlug] = useState(null);

  const toggleDetails = (slug) => {
    setExpandedSlug(expandedSlug === slug ? null : slug);
    setOpenFaq(null);
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="indicators-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 text-center relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-sky-400/[0.03] blur-[120px]" />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400/60 mb-3">Indicators & Tools</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">Professional MT5 Indicators</h1>
          <p className="mt-5 text-sm text-white/40 max-w-2xl mx-auto leading-relaxed">
            Quantitative analysis tools built for serious traders. Each indicator comes with full source code (.mq5), comprehensive documentation, and a lifetime license.
          </p>
        </div>
      </section>

      {/* Product Cards with expandable Details */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 border-t border-white/[0.04]" data-testid="indicator-products">
        <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {INDICATORS_DATA.map(ind => {
            const inCart = items.some(i => i.slug === ind.slug);
            const isExpanded = expandedSlug === ind.slug;
            return (
              <div key={ind.slug} className={`rounded-2xl border bg-[#0a0f1c] overflow-hidden transition-colors ${isExpanded ? 'border-amber-500/30' : 'border-white/10'}`} data-testid={`indicator-${ind.slug}`}>
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={ind.image_url} alt={ind.name} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-500/90 text-[#050814]">{ind.platform}</span>
                </div>
                <div className="p-5">
                  <h2 className="text-lg font-bold text-white">{ind.name}</h2>
                  <p className="text-xs text-white/40 mt-0.5">{ind.subtitle}</p>
                  <ul className="mt-4 space-y-2">
                    {ind.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-white/50">
                        <Check size={13} className="text-amber-400 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-bold text-white">${ind.price}</span>
                      <span className="text-xs text-white/30 ml-1">USD</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleDetails(ind.slug)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${isExpanded ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' : 'text-white/50 border-white/10 hover:border-white/20'}`}
                        data-testid={`details-${ind.slug}`}
                      >
                        {isExpanded ? 'Hide Details' : 'Details'}
                      </button>
                      <button
                        onClick={() => addItem(ind)}
                        disabled={inCart}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${inCart ? 'bg-white/5 text-white/30' : 'bg-amber-500/10 text-amber-500 border border-amber-500/30 hover:bg-amber-500/20'}`}
                        data-testid={`add-${ind.slug}`}
                      >
                        {inCart ? 'In Cart' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expandable How It Works */}
                {isExpanded && ind.slug === 'arbiedge-pro' && (
                  <ArbiEdgeDetails openFaq={openFaq} setOpenFaq={setOpenFaq} />
                )}
                {isExpanded && ind.slug === 'traffic-light-suite' && (
                  <TrafficLightDetails openFaq={openFaq} setOpenFaq={setOpenFaq} />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 9-Step Methodology */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="methodology-section">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold text-white mb-2">The 9-Step Methodology</h2>
          <p className="text-sm text-white/40 mb-8">Each step must be completed in sequence. If any step fails, the system resets and waits. This is how Hettie eliminated emotion from trading.</p>
          <div className="flex items-center gap-3 mb-8 text-xs font-semibold">
            <span className="px-3 py-1 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/20">H1 — Direction</span>
            <span className="px-3 py-1 rounded-full bg-sky-400/10 text-sky-400 border border-sky-400/20">M15 — Bias</span>
            <span className="px-3 py-1 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">M5 — Entry</span>
          </div>
          <div className="space-y-4">
            {STEPS.map((s) => {
              const tfColor = s.tf === 'H1' ? 'text-amber-400' : s.tf === 'M15' ? 'text-sky-400' : 'text-emerald-400';
              return (
                <div key={s.num} className="flex gap-5 items-start rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-amber-400/10 transition-colors" data-testid={`step-${s.num}`}>
                  <div className="flex flex-col items-center shrink-0">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-400/10 text-amber-400 text-sm font-bold">{s.num}</span>
                    <span className={`mt-1 text-[10px] font-semibold uppercase ${tfColor}`}>{s.tf}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white/90 text-sm">{s.title}</h3>
                    <p className="mt-1 text-xs text-white/40 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-white/30 italic">All 9 steps must complete in sequence. Steps 1–7 are confirmed on the H1 chart, Step 8 on M15, and Step 9 on M5 for final entry.</p>
        </div>
      </section>

      {/* 5 EAs — Two Strategies */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="eas-section">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold text-white mb-2">5 EAs — Two Strategies</h2>
          <p className="text-sm text-white/40 mb-8">Two distinct strategies: the <span className="text-sky-400 font-medium">Bowtie Scalping System</span> for quick intraday trades, and the <span className="text-amber-400 font-medium">9-Step Day Trading System</span> for larger swing setups.</p>
          <div className="space-y-3">
            {EAS.map(ea => (
              <div key={ea.num} className="flex items-center gap-5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-amber-400/10 transition-colors" data-testid={`ea-${ea.num}`}>
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-white/50 text-sm font-bold shrink-0">{ea.num}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white/90 text-sm">{ea.name}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/40">{ea.level}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-white/40">{ea.desc}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${ea.tier === 'Bowtie' ? 'bg-sky-400/10 text-sky-400' : ea.tier === '9-Step' ? 'bg-white/5 text-white/40' : 'bg-amber-400/10 text-amber-400'}`}>{ea.tier}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04] text-center" data-testid="indicators-cta">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-bold text-white">See the Market Differently</h2>
          <p className="mt-3 text-sm text-white/40">Professional-grade analysis tools for MetaTrader 5. One-time purchase, lifetime access, full source code included.</p>
          <div className="mt-6 flex items-center justify-center gap-4 text-sm">
            <span className="text-white/60">ArbiEdge Pro — $97</span>
            <span className="text-white/20">|</span>
            <span className="text-white/60">Traffic Light Suite — $147</span>
          </div>
          <p className="mt-4 text-[10px] text-white/20">Secure payment via Stripe - Instant download after purchase</p>
        </div>
      </section>
    </div>
  );
}
